import { Box } from "@mui/material";
import { sortBy } from "lodash";
import { ReactElement, useCallback, useMemo, useState } from "react";

import {
  CampaignStatus,
  SuppressionStage,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { CampaignWithSuppressionData } from "~frontend/routes/types";
import { jobInProgress } from "~frontend/util/jobs";

import { StepInstructions } from "../StepInstructions";
import SaveButtons from "../campaignEditorUtils/SaveButtons";
import OverlapList from "./OverlapList";
import { SuppressShortcodeSubscribers } from "./SuppressShortcodeSubscribers";
import SuppressionErrors from "./SuppressionErrors";
import SuppressionsList from "./SuppressionsList";
import { Suppression } from "./SuppressionsList/types";

export default function Suppressions({
  campaign,
  goToNextSection,
}: {
  campaign: CampaignWithSuppressionData;
  goToNextSection: (opts?: { bypassRouteLeavingGuard?: boolean }) => void;
}): ReactElement {
  const [overlapListExpanded, setOverlapListExpanded] = useState(true);
  const [preLaunchSuppressionsExpanded, setPreLaunchSuppressionsExpanded] =
    useState(campaign.status === CampaignStatus.Draft);
  const [postLaunchSuppressionsExpanded, setPostLaunchSuppressionsExpanded] =
    useState(true);

  const campaignStartedOrStarting =
    campaign.status !== CampaignStatus.Draft ||
    (!!campaign.campaignStartJob && jobInProgress(campaign.campaignStartJob));

  const campaignDoesNotHaveContacts =
    !campaign.contactImportJob || jobInProgress(campaign.contactImportJob);

  const suppressionsForStage = useCallback(
    (stage: SuppressionStage) => {
      const campaignOverlaps = campaign.campaignOverlaps
        .filter(o => o.appliedAt && o.appliedStage === stage)
        .map(
          o =>
            ({
              kind: "overlap",
              id: o.id,
              name: o.overlappingCampaign.name,
              appliedByFullname: o.appliedByUser?.fullName ?? "Unknown",
              appliedAt: new Date(o.appliedAt),
              count: o.overlapCount,
              filter: o.appliedWithFilterType,
              filterTagIds: o.appliedWithFilterTagIds,
              campaignCreatedAt: new Date(o.overlappingCampaign.createdAt),
            }) as const
        );
      const suppressionLists = campaign.suppressionLists
        .filter(l => l.stage === stage)
        .map(
          l =>
            ({
              kind: "list",
              id: l.id,
              name: l.upload.fileName,
              appliedByFullname: l.createdByUser?.fullName ?? "Unknown",
              appliedAt: new Date(l.createdAt),
              count: l.suppressedCount,
            }) as const
        );
      const shortcodeSubscriberSuppressions =
        campaign.shortcodeSubscriberSuppressions
          .filter(s => s.stage === stage)
          .map(
            s =>
              ({
                kind: "shortcodeSubscribers",
                id: s.id,
                name: "Shortcode Subscribers",
                appliedByFullname: s.createdByUser.fullName,
                appliedAt: new Date(s.createdAt),
                count: s.suppressedCount,
              }) as const
          );

      return sortBy(
        [
          ...campaignOverlaps,
          ...suppressionLists,
          ...shortcodeSubscriberSuppressions,
        ],
        s => -s.appliedAt
      );
    },
    [
      campaign.campaignOverlaps,
      campaign.suppressionLists,
      campaign.shortcodeSubscriberSuppressions,
    ]
  );

  const preLaunchSuppressions: Suppression[] = useMemo(
    () => suppressionsForStage(SuppressionStage.PreStart),
    [suppressionsForStage]
  );

  const postLaunchSuppressions: Suppression[] = useMemo(
    () => suppressionsForStage(SuppressionStage.PostStart),
    [suppressionsForStage]
  );

  return (
    <FlexBox direction="column" gap={4}>
      <Box>
        <StepInstructions header="Suppressions">
          {campaignDoesNotHaveContacts ? (
            <>
              Please upload contacts or wait until they are imported before
              adding suppressions.
            </>
          ) : (
            <>
              <Box>
                Suppress contacts from this campaign by uploading a list of
                phone numbers to exclude or by selecting previous campaigns with
                overlapping contacts you&apos;d like to remove from this
                campaign.
              </Box>
              <Box>
                <strong>Note</strong>:{" "}
                {campaignStartedOrStarting ? (
                  <>
                    Total Unique Suppressions may differ from the total
                    displayed below, as the campaign is already running and some
                    contacts may have been messaged already.
                  </>
                ) : (
                  <>
                    Suppressions cannot be removed once the campaign has
                    started.
                  </>
                )}
              </Box>
            </>
          )}
        </StepInstructions>
      </Box>

      {!campaignDoesNotHaveContacts && (
        <>
          <OverlapList
            campaign={campaign}
            expanded={overlapListExpanded}
            onToggleExpanded={() =>
              setOverlapListExpanded(!overlapListExpanded)
            }
            onApplySuppressions={() => {
              setOverlapListExpanded(false);
              setPreLaunchSuppressionsExpanded(
                campaign.status === CampaignStatus.Draft
              );
              setPostLaunchSuppressionsExpanded(
                campaign.status !== CampaignStatus.Draft
              );
            }}
          />
          <SuppressionErrors campaign={campaign} />
          <SuppressShortcodeSubscribers campaign={campaign} />
          {campaign.status === CampaignStatus.Draft ||
          preLaunchSuppressions.length > 0 ? (
            <SuppressionsList
              campaign={campaign}
              suppressions={preLaunchSuppressions}
              campaignStartedOrStarting={campaignStartedOrStarting}
              stage={SuppressionStage.PreStart}
              expanded={preLaunchSuppressionsExpanded}
              onToggleExpanded={() =>
                setPreLaunchSuppressionsExpanded(!preLaunchSuppressionsExpanded)
              }
              onApplySuppressions={() => {
                setOverlapListExpanded(false);
                setPreLaunchSuppressionsExpanded(true);
              }}
            />
          ) : null}
          {campaign.status === CampaignStatus.Draft ? null : (
            <SuppressionsList
              campaign={campaign}
              suppressions={postLaunchSuppressions}
              campaignStartedOrStarting={campaignStartedOrStarting}
              stage={SuppressionStage.PostStart}
              expanded={postLaunchSuppressionsExpanded}
              onToggleExpanded={() =>
                setPostLaunchSuppressionsExpanded(
                  !postLaunchSuppressionsExpanded
                )
              }
              onApplySuppressions={() => {
                setOverlapListExpanded(false);
                setPreLaunchSuppressionsExpanded(false);
                setPostLaunchSuppressionsExpanded(true);
              }}
            />
          )}
        </>
      )}

      <Box>
        <SaveButtons
          variant="continueOnly"
          handleSave={() => goToNextSection()}
        />
      </Box>
    </FlexBox>
  );
}
